export const VALID_LANGUAGES = ['en', 'zh', 'es', 'fr', 'ar', 'ja', 'tw', 'ko'] as const

export type LanguageCode = (typeof VALID_LANGUAGES)[number]

const LANGUAGE_FROM_PATH: Record<string, LanguageCode> = {
  'en': 'en',
  'zh-hans': 'zh',
  'zh-hant': 'tw',
  'ja': 'ja',
  'ko': 'ko',
  'ar': 'ar',
  'es': 'es',
  'fr': 'fr',
}

const PATH_FROM_LANGUAGE: Record<LanguageCode, string> = {
  en: 'en',
  zh: 'zh-hans',
  tw: 'zh-hant',
  ja: 'ja',
  ko: 'ko',
  ar: 'ar',
  es: 'es',
  fr: 'fr',
}

export function getPathLanguagePrefix(pathname: string): string | null {
  const segment = /^\/([^/]+)(?:\/|$)/.exec(pathname)?.[1]
  if (!segment || !Object.hasOwn(LANGUAGE_FROM_PATH, segment))
    return null
  return `/${segment}`
}

export function getPathLanguage(pathname: string): LanguageCode | null {
  const prefix = getPathLanguagePrefix(pathname)
  return prefix ? LANGUAGE_FROM_PATH[prefix.slice(1)]! : null
}

export function stripLanguagePrefix(pathname: string): string {
  const prefix = getPathLanguagePrefix(pathname)
  return prefix ? pathname.slice(prefix.length) || '/' : pathname
}

export function withLanguagePath(pathname: string, code: string): string {
  const language = isValidLanguageCode(code) ? code : 'en'
  const prefix = `/${PATH_FROM_LANGUAGE[language]}`
  const rest = stripLanguagePrefix(pathname)
  return rest === '/' ? prefix : `${prefix}${rest}`
}

function languageFromLegacyParameter(
  code: string | null | undefined,
): LanguageCode | null {
  if (!code)
    return null
  if (isValidLanguageCode(code))
    return code
  if (code === 'zh-hans')
    return 'zh'
  if (code === 'zh-hant')
    return 'tw'
  return null
}

export function getLegacyQueryLanguage(search: string): LanguageCode | null {
  try {
    return languageFromLegacyParameter(new URLSearchParams(search).get('lang'))
  }
  catch {
    return null
  }
}

export function resolveInterfaceLanguage(input: {
  path?: string | null
  explicit?: string | null
  stored?: string | null
  browser?: readonly string[] | null
}): LanguageCode {
  const pathLanguage = input.path ? getPathLanguage(input.path) : null
  if (pathLanguage)
    return pathLanguage

  const explicitLanguage = languageFromLegacyParameter(input.explicit)
  if (explicitLanguage)
    return explicitLanguage

  if (input.stored && isValidLanguageCode(input.stored))
    return input.stored

  for (const tag of input.browser ?? []) {
    const resolved = languageFromBrowserTag(tag)
    if (resolved)
      return resolved
  }

  return 'en'
}

function languageFromBrowserTag(tag: string): LanguageCode | null {
  if (typeof tag !== 'string')
    return null
  const normalized = tag.trim().toLowerCase()
  if (normalized.length === 0)
    return null

  const [base, ...rest] = normalized.split('-')
  if (!base)
    return null

  if (base === 'zh') {
    const region = rest.join('-')
    if (region.includes('hant') || region.includes('tw') || region.includes('hk') || region.includes('mo'))
      return 'tw'
    return 'zh'
  }

  return isValidLanguageCode(base) ? base : null
}

export function defaultLobbyLanguageFilter(
  interfaceLanguage: string,
): LanguageCode[] {
  const resolved = isValidLanguageCode(interfaceLanguage)
    ? interfaceLanguage
    : 'en'
  return resolved === 'en' ? ['en'] : [resolved, 'en']
}

export function getCurrentLanguageFromUrl(): LanguageCode {
  if (typeof window === 'undefined')
    return 'en'

  const explicit = getLegacyQueryLanguage(window.location.search)

  const resolved = resolveInterfaceLanguage({
    path: window.location.pathname,
    explicit,
  })

  if (
    getPathLanguage(window.location.pathname)
    || explicit
  ) {
    try {
      localStorage.setItem('lang', resolved)
    }
    catch {}
  }

  return resolved
}

export function updateUrlWithLanguage(
  language: string,
  addToHistory = false,
): void {
  if (typeof window === 'undefined')
    return

  try {
    const currentUrl = new URL(window.location.href)
    const targetUrl = new URL(
      createUrlWithLanguage(language, currentUrl.toString()),
    )
    if (targetUrl.toString() === currentUrl.toString())
      return

    if (addToHistory)
      window.location.assign(targetUrl.toString())
    else window.location.replace(targetUrl.toString())
  }
  catch {}
}

export function removeLanguageFromUrl(addToHistory = false): void {
  if (typeof window === 'undefined')
    return

  try {
    const url = new URL(window.location.href)
    url.searchParams.delete('lang')

    if (addToHistory)
      window.history.pushState({}, '', url.toString())
    else window.history.replaceState({}, '', url.toString())
  }
  catch {}
}

export function isValidLanguageCode(code: string): code is LanguageCode {
  return VALID_LANGUAGES.includes(code as LanguageCode)
}

export function getCleanUrl(): string {
  if (typeof window === 'undefined')
    return ''

  try {
    const url = new URL(window.location.href)
    url.searchParams.delete('lang')
    return url.toString()
  }
  catch {
    return window.location.href
  }
}

export function createUrlWithLanguage(
  language: string,
  baseUrl?: string,
): string {
  const source
    = baseUrl ?? (typeof window === 'undefined' ? '' : window.location.href)
  try {
    const url = new URL(source)
    url.pathname = withLanguagePath(url.pathname, language)
    url.searchParams.delete('lang')
    return url.toString()
  }
  catch {
    return source
  }
}

export function createUrlWithLanguageAndSection(
  language: string,
  sectionId: string,
  baseUrl?: string,
): string {
  const source = createUrlWithLanguage(language, baseUrl)
  try {
    const url = new URL(source)
    url.hash = `#${sectionId}`
    return url.toString()
  }
  catch {
    return source
  }
}
